'use strict';
/* ==========================================================================
   core-local/paper_reader.js — window.PaperReader: a read-along "paper" as a DOM overlay inside a lesson step
   (the Pamphlet pilot; why DOM and the full API: core-local/PAPER_READER.md). Lesson-agnostic: it knows the paper
   JSON shape (content/paper.json, PAPER.md) and nothing about the narrator, the audio or the stage; the step
   (levels/pamphlet.js) drives it.

     PaperReader.model(json)                 → the paper as data: {seq (every voiced sentence, in reading order), sections,
                                               glossary, summary, sources, front}
     const r = PaperReader.create(model, handlers)
         handlers: onSentence(id) onTerm(term, sectionId) onCheck(secId, i) onSkip(secId) onContinue(secId) onBack()
                   onUserScroll() onEnd(action)  drawFigure(canvas, fig, {hi, fs})  speaker(who) → {name, color}
     r.layout({ rect: {left, top, width, height} (CSS px, viewport), fs (CSS px), wide (figures beside the text: the
              inline canvases hide), stage: rect for term cards })
     r.show(on)             r.destroy()
     r.setCurrent(id, word) the highlighted sentence (+ its word index, −1 = none)
     r.setFollow(on, chip)  the "Back to the reader" chip (shown when chip && !on)
     r.reveal(id | el, {smooth, force})  scroll so it sits at ~28 % of the column (follow mode); r.tick(dt) animates
     r.visible(id | el)     on screen in the column?      r.sectionInView() → the section id at the reading line
     r.setCheck(secId, {order, wrong: [i…], right: i|null, skipped, waiting}) / r.setEnd({show, line, score})
     r.showTerm(term) / r.hideTerm()   the definition card (over the stage / figure area: never over the text)
     r.figure(secId) → its inline canvas;  r.refreshFigures(hiBySec)
     r.el / r.col / r.debug()
   ========================================================================== */
(function () {
  const COL = { ink: '#2b2420', navy: '#1f2f55', brick: '#9e2b25', cream: '#fbf5e6', paper: '#f3ead7', mustard: '#f2b33d', gold: '#b08a2e', green: '#4f7d3a' };
  const CSS = `
  #pp-root { position: fixed; z-index: 6; display: none; color: ${COL.ink}; font: 400 var(--fs, 17px)/1.55 Georgia, 'Times New Roman', serif;
    -webkit-user-select: none; user-select: none; -webkit-tap-highlight-color: transparent; }
  #pp-root.on { display: block; }
  #pp-root *, #pp-root *::before, #pp-root *::after { box-sizing: border-box; }
  #pp-root button { font: inherit; color: inherit; border: 0; margin: 0; cursor: pointer; touch-action: manipulation; background: none; }
  #pp-root :focus { outline: none; } #pp-root :focus-visible { outline: 3px solid ${COL.navy}; outline-offset: 2px; }
  .pp-col { position: absolute; inset: 0; overflow-y: auto; overflow-x: hidden; overscroll-behavior: contain; -webkit-overflow-scrolling: touch;
    padding: 1.1em 1.2em 4.5em; scrollbar-width: thin; scrollbar-color: rgba(43,36,32,.35) transparent; touch-action: pan-y; }
  #pp-root.dock .pp-col { padding: 1.4em .9em 4.5em; }
  .pp-paper { max-width: 40em; margin: 0 auto; }
  .pp-kicker { margin: 0 0 .3em; font: 400 .78em/1.3 'Special Elite', 'Courier New', monospace; color: ${COL.brick}; letter-spacing: .04em; text-transform: uppercase; }
  .pp-title { margin: 0 0 .35em; font: 400 1.62em/1.18 'Alfa Slab One', Georgia, serif; color: ${COL.ink}; }
  .pp-byline { margin: 0 0 .2em; font-style: italic; color: rgba(43,36,32,.85); }
  .pp-meta { margin: 0 0 .9em; font: 400 .78em/1.3 'Special Elite', monospace; color: rgba(31,47,85,.85); }
  .pp-rule { height: 0; border: 0; border-top: 3px double rgba(43,36,32,.55); margin: .6em 0 .9em; }
  .pp-h2 { margin: 1.3em 0 .45em; font: 900 1.28em/1.2 'Playfair Display', Georgia, serif; color: ${COL.navy}; }
  .pp-hbtn { display: flex; align-items: baseline; gap: .5em; width: 100%; min-height: 44px; padding: .15em 0; text-align: left; font: inherit !important; color: inherit !important; }
  .pp-hbtn:hover .pp-s { text-decoration: underline; text-decoration-color: rgba(31,47,85,.35); }
  .pp-num { flex: none; display: inline-flex; align-items: center; justify-content: center; width: 1.55em; height: 1.55em; border-radius: 50%;
    background: ${COL.brick}; color: ${COL.cream}; font: 400 .72em/1 'Alfa Slab One', serif; transform: translateY(-.1em); }
  .pp-abs { margin: 0 0 1em; padding: .55em .8em; border-left: 4px solid ${COL.navy}; background: rgba(31,47,85,.05); }
  .pp-abs-k { margin: 0 0 .2em; font: 400 .74em/1.2 'Special Elite', monospace; color: ${COL.navy}; letter-spacing: .06em; text-transform: uppercase; }
  .pp-p { margin: 0 0 .8em; text-align: left; hyphens: manual; }
  .pp-s { cursor: pointer; border-radius: 3px; transition: background-color .15s, box-shadow .15s; -webkit-box-decoration-break: clone; box-decoration-break: clone; }
  .pp-s:hover { background: rgba(242,179,61,.14); }
  .pp-s.cur { background: rgba(242,179,61,.42); box-shadow: 0 0 0 .14em rgba(242,179,61,.42); }
  .pp-s.read { color: rgba(43,36,32,.92); }
  .pp-s .w.on { background: ${COL.mustard}; box-shadow: 0 0 0 .1em ${COL.mustard}; border-radius: 2px; }
  .pp-alt { display: block; margin: .45em 0 .55em .2em; padding: .2em .6em .25em; border-left: 4px solid var(--c, ${COL.brick});
    font: 400 1.12em/1.3 'Caveat Brush', 'Comic Sans MS', cursive; color: var(--c, ${COL.brick}); transform: rotate(-.6deg); }
  .pp-alt.cur { box-shadow: none; }
  .pp-who { display: inline-block; margin-right: .45em; padding: .08em .45em 0; font: 400 .62em/1.4 'Alfa Slab One', serif; letter-spacing: .04em;
    color: ${COL.cream}; background: var(--c, ${COL.brick}); transform: rotate(-3deg) translateY(-.1em); }
  .pp-t { position: relative; color: ${COL.navy}; text-decoration: underline dotted rgba(31,47,85,.75); text-decoration-thickness: 2px; text-underline-offset: .18em; cursor: pointer; }
  .pp-t::before { content: ''; position: absolute; left: -4px; right: -4px; top: -12px; bottom: -12px; }
  .pp-alt .pp-t { color: inherit; text-decoration-color: currentColor; }
  .pp-t:hover { background: rgba(140,199,217,.3); }
  .pp-fig { margin: .3em 0 1em; }
  .pp-fig canvas, .pp-figcopy { display: block; width: 100%; }
  .pp-figcopy { margin: 0 0 .5em; }
  #pp-root.dock .pp-figcopy { display: none; }   /* portrait: the stage above already shows the section's figure */
  .pp-fig figcaption, .pp-figcap { margin: .25em 0 0; font: 400 .74em/1.35 'Special Elite', monospace; color: rgba(43,36,32,.78); }
  #pp-root.wide .pp-fig canvas, #pp-root.wide .pp-figcopy { display: none; }
  #pp-root.wide .pp-fig { margin: 0 0 .8em; padding: .3em .6em; border: 1px dashed rgba(43,36,32,.3); }
  .pp-card { margin: .4em 0 1em; padding: .6em .8em .5em; background: #fff8df; border: 2px solid rgba(43,36,32,.18); box-shadow: 2px 3px 0 rgba(43,36,32,.15); transform: rotate(-.4deg); }
  .pp-card.heads { background: #fde9df; border-color: rgba(158,43,37,.45); }
  .pp-card-k { margin: 0 0 .25em; font: 400 .95em/1.1 'Luckiest Guy', Impact, sans-serif; letter-spacing: .04em; color: ${COL.brick}; }
  .pp-card.ask .pp-card-k { color: ${COL.navy}; }
  .pp-card p { margin: 0 0 .35em; }
  .pp-card.ask { padding: .3em .5em; }
  .pp-ask-tab { display: flex; align-items: center; width: 100%; min-height: 44px; padding: .2em .4em; text-align: left;
    font: 400 .95em/1.1 'Luckiest Guy', Impact, sans-serif !important; letter-spacing: .04em; color: ${COL.navy} !important; }
  .pp-ask-tab::after { content: '+'; margin-left: auto; font: 400 1.3em/1 'Alfa Slab One', serif; color: ${COL.brick}; }
  .pp-ask-tab[aria-expanded=true]::after { content: '–'; }
  .pp-ask-body { padding: .2em .4em .3em; }
  .pp-qa { margin: 0 0 .5em; }
  .pp-q { margin: 0; font: 400 1.08em/1.25 'Caveat Brush', cursive; color: ${COL.brick}; }
  .pp-a { margin: .05em 0 0; }
  .pp-check { margin: .6em 0 1.2em; padding: .7em .8em .6em; background: ${COL.cream}; border: 2px solid ${COL.navy}; box-shadow: 3px 4px 0 rgba(31,47,85,.2); }
  .pp-check.waiting { box-shadow: 0 0 0 4px ${COL.mustard}, 3px 6px 0 4px rgba(31,47,85,.2); }
  .pp-check-k { margin: 0 0 .2em; font: 400 .74em/1.2 'Special Elite', monospace; color: ${COL.navy}; letter-spacing: .06em; text-transform: uppercase; }
  .pp-prompt { margin: 0 0 .5em; font-weight: 700; color: ${COL.navy}; }
  .pp-opts { display: flex; flex-direction: column; gap: 8px; }
  .pp-opt { display: flex; align-items: center; gap: .6em; width: 100%; min-height: 48px; padding: .35em .7em; text-align: left; line-height: 1.25 !important;
    background: #fffdf6 !important; border: 2px solid rgba(43,36,32,.3) !important; border-radius: 3px; box-shadow: 1px 2px 0 rgba(43,36,32,.2); }
  .pp-opt:hover { border-color: ${COL.navy} !important; }
  .pp-opt .n { flex: none; display: inline-flex; align-items: center; justify-content: center; width: 1.7em; height: 1.7em; border-radius: 50%;
    background: ${COL.navy}; color: ${COL.cream}; font: 400 .8em/1 'Alfa Slab One', serif; }
  .pp-opt .t { flex: 1 1 auto; }
  .pp-opt .mk { flex: none; font: 400 .85em/1 'Luckiest Guy', Impact, sans-serif; letter-spacing: .03em; }
  .pp-opt.right { border-color: ${COL.green} !important; background: #eef6e6 !important; } .pp-opt.right .mk { color: ${COL.green}; }
  .pp-opt.wrong { border-color: ${COL.brick} !important; background: #fbe9e4 !important; opacity: .85; } .pp-opt.wrong .mk { color: ${COL.brick}; }
  .pp-opt[aria-disabled=true] { cursor: default; }
  .pp-why { margin: .45em 0 0; min-height: 0; }
  .pp-why .ok { color: ${COL.green}; font-weight: 700; } .pp-why .no { color: ${COL.brick}; font-weight: 700; }
  .pp-cbtns { display: flex; flex-wrap: wrap; gap: 10px; margin: .55em 0 0; }
  .pp-btn { min-height: 48px; min-width: 48px; padding: .35em 1em; color: ${COL.cream} !important; background: ${COL.navy} !important; border-radius: 3px;
    font: 400 1.05em/1.1 'Caveat Brush', cursive !important; box-shadow: 2px 3px 0 rgba(43,36,32,.3); }
  .pp-btn.ghost { color: ${COL.navy} !important; background: transparent !important; border: 2px solid rgba(31,47,85,.45) !important; box-shadow: none; }
  .pp-btn.red { background: ${COL.brick} !important; }
  .pp-terms dl { margin: 0; } .pp-terms dt { margin: .5em 0 0; font-weight: 700; color: ${COL.navy}; } .pp-terms dd { margin: 0 0 0 1em; }
  .pp-sum { padding: .3em .9em .6em; background: rgba(176,138,46,.1); border-top: 3px double rgba(43,36,32,.5); border-bottom: 3px double rgba(43,36,32,.5); }
  .pp-sum ul { margin: 0; padding: 0 0 0 1.1em; } .pp-sum li { margin: 0 0 .5em; }
  .pp-score { margin: .5em 0 .2em; font: 400 1.1em/1.2 'Caveat Brush', cursive; color: ${COL.navy}; }
  .pp-end { margin: .6em 0 .3em; } .pp-end p { margin: 0 0 .4em; }
  .pp-src ol { margin: 0; padding: 0 0 0 1.4em; font-size: .86em; } .pp-src li { margin: 0 0 .55em; }
  .pp-src a { color: ${COL.navy}; word-break: break-word; overflow-wrap: anywhere; display: inline-block; padding: 4px 0; }
  .pp-src .u { display: block; font: 400 .85em/1.3 'Special Elite', monospace; color: rgba(31,47,85,.85); overflow-wrap: anywhere; }
  #pp-root.chip .pp-col { bottom: 64px; }
  .pp-chip { position: absolute; left: 50%; bottom: 6px; transform: translateX(-50%) rotate(-1deg); min-height: 48px; padding: .35em 1.1em; white-space: nowrap;
    color: ${COL.ink} !important; background: ${COL.mustard} !important; border-radius: 24px; font: 400 1.05em/1.1 'Caveat Brush', cursive !important;
    box-shadow: 2px 4px 0 rgba(43,36,32,.35); z-index: 2; }
  .pp-chip[hidden] { display: none; }
  #pp-term { position: fixed; z-index: 7; display: none; color: ${COL.ink}; font: 400 var(--fs, 17px)/1.4 Georgia, serif; }
  #pp-term.on { display: flex; }
  #pp-term .box { position: relative; display: flex; flex-direction: column; max-width: 100%; max-height: 100%; overflow: auto; padding: .55em .8em .5em;
    background: #fffdf3; border: 2px solid ${COL.navy}; box-shadow: 4px 6px 0 rgba(31,47,85,.3); transform: rotate(-.6deg); }
  #pp-term h3 { margin: 0 0 .25em; font: 900 1.15em/1.15 'Playfair Display', Georgia, serif; color: ${COL.navy}; }
  #pp-term p { margin: 0 0 .4em; }
  #pp-term button { align-self: flex-end; min-height: 44px; min-width: 88px; padding: .2em .9em; color: ${COL.cream}; background: ${COL.navy}; border: 0; border-radius: 3px;
    font: 400 1em/1 'Caveat Brush', cursive; cursor: pointer; }
  @media (prefers-reduced-motion: reduce) { .pp-s { transition: none; } }
  `;
  const h = (tag, cls, attrs) => {
    const e = document.createElement(tag); if (cls) e.className = cls;
    if (attrs) for (const k in attrs) { if (k === 'text') e.textContent = attrs[k]; else e.setAttribute(k, attrs[k]); }
    return e;
  };

  /* ---------------- the paper as data ---------------- */
  function model(json) {
    const J = json || {}, nar = (J.narrator && (J.narrator.default || J.narrator.who)) || 'judge';
    const seq = [];
    const add = (s, sec, kind, extra) => { if (s && s.id) seq.push(Object.assign({ id: s.id, text: s.text || '', tts: s.tts || null, who: s.who || nar, sec, kind }, extra)); };
    add(J.title, 'front', 'title');
    (J.abstract || []).forEach(s => add(s, 'front', 'abs'));
    const sections = (J.sections || []).map((S, si) => {
      add(S.heading, S.id, 'head');
      (S.paragraphs || []).forEach((p, pi) => (p || []).forEach((s, k) => add(s, S.id, 's', { para: pi, paraEnd: k === p.length - 1 })));
      const cards = S.card == null ? [] : Array.isArray(S.card) ? S.card : [S.card];
      return { id: S.id, n: S.n != null ? S.n : si + 1, heading: S.heading, paragraphs: S.paragraphs || [], figure: S.figure || null, terms: S.terms || [], cards, check: S.check || null };
    });
    const sum = J.summary || {};
    add(sum.heading, 'summary', 'head');
    (sum.lines || []).forEach(s => add(s, 'summary', 'sum'));
    // the last voiced sentence of each section: where its check waits
    const lastOf = {};
    seq.forEach((s, i) => { lastOf[s.sec] = i; });
    seq.forEach((s, i) => { s.i = i; s.secEnd = lastOf[s.sec] === i; });
    const glossary = [];
    sections.forEach(S => S.terms.forEach(t => glossary.push(Object.assign({ sec: S.id }, t))));
    glossary.sort((a, b) => a.term.localeCompare(b.term, 'en', { sensitivity: 'base' }) || (a.term < b.term ? 1 : -1));
    return { json: J, narrator: nar, seq, sections, glossary, summary: sum, sources: J.sources || [],
      front: { title: J.title || {}, subtitle: J.subtitle || '', readingTime: J.readingTime || '', abstract: J.abstract || [], step: J.step || {} } };
  }

  /* tokens: a sentence's words as shown (punctuation attached), and a normalized form for matching */
  const words = s => String(s).split(/\s+/).filter(Boolean);
  const normW = (w, keepCase) => { let t = String(w).replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, '').replace(/['’]s$/, ''); return keepCase ? t : t.toLowerCase(); };

  /* the first place each term appears: its own section first, then anywhere after it */
  function findTerms(M) {
    const all = M.glossary.map(t => t.term), lowerTwin = t => all.some(o => o !== t && o.toLowerCase() === t.toLowerCase());
    const out = {};      // sentence id → [{start, len, term}]
    const used = new Set();
    const secIndex = {}; M.sections.forEach((S, i) => { secIndex[S.id] = i; });
    // longest terms first so "Anti-Federalists" wins over "Federalists", "bill of rights" over "rights"
    const terms = M.glossary.slice().sort((a, b) => words(b.term).length - words(a.term).length || b.term.length - a.term.length);
    for (const t of terms) {
      const keep = lowerTwin(t.term), tw = words(t.term).map(w => normW(w, keep));
      const order = M.seq.filter(s => s.kind !== 'title').slice().sort((a, b) => {
        const ka = a.sec === t.sec ? 0 : (secIndex[a.sec] != null && secIndex[a.sec] > secIndex[t.sec] ? 1 : 2);
        const kb = b.sec === t.sec ? 0 : (secIndex[b.sec] != null && secIndex[b.sec] > secIndex[t.sec] ? 1 : 2);
        return ka - kb || a.i - b.i;
      });
      for (const s of order) {
        if (s.kind === 'head') continue;
        const ws = words(s.text).map(w => normW(w, keep));
        let hit = -1;
        for (let i = 0; i + tw.length <= ws.length && hit < 0; i++) if (tw.every((w, k) => ws[i + k] === w || (k === tw.length - 1 && ws[i + k] === w + 's'))) hit = i;   // a plural counts too
        if (hit < 0) continue;
        const taken = (out[s.id] || []).some(r => hit < r.start + r.len && r.start < hit + tw.length);
        if (taken) continue;
        (out[s.id] = out[s.id] || []).push({ start: hit, len: tw.length, term: t });
        used.add(t.term);
        break;
      }
    }
    return out;
  }

  /* ---------------- the reader ---------------- */
  function create(M, H = {}) {
    if (!document.getElementById('pp-css')) { const st = h('style'); st.id = 'pp-css'; st.textContent = CSS; document.head.appendChild(st); }
    const root = h('div', '', { id: 'pp-root', role: 'region', 'aria-label': (M.front.step.title || 'The paper') + ': read-along' });
    const col = h('div', 'pp-col');
    const paper = h('article', 'pp-paper', { lang: 'en' });
    col.appendChild(paper); root.appendChild(col);
    const chip = h('button', 'pp-chip', { type: 'button', text: 'Back to the reader', 'aria-label': 'Back to the reader' });
    chip.hidden = true; root.appendChild(chip);
    const term = h('div', '', { id: 'pp-term', role: 'dialog', 'aria-modal': 'false', 'aria-label': 'definition' });
    const tbox = h('div', 'box'), tH = h('h3'), tP = h('p'), tBtn = h('button', '', { type: 'button', text: 'Got it' });
    tbox.append(tH, tP, tBtn); term.appendChild(tbox);
    document.body.append(root, term);

    const sEl = {}, wEls = {}, secEl = {}, checkEl = {}, figCanvas = {}, figCopy = {};
    const termsAt = findTerms(M);
    const spk = who => (H.speaker && H.speaker(who)) || { name: who, color: COL.brick };

    function sentence(s, extraCls = '') {
      const alt = s.who && s.who !== M.narrator;
      const el = h('span', 'pp-s' + (alt ? ' pp-alt' : '') + (extraCls ? ' ' + extraCls : ''), { 'data-id': s.id });
      if (alt) { const sp = spk(s.who); el.style.setProperty('--c', sp.color); el.appendChild(h('span', 'pp-who', { text: sp.name, 'aria-hidden': 'false' })); }
      const ws = words(s.text), marks = (termsAt[s.id] || []).slice().sort((a, b) => a.start - b.start);
      const list = [];
      let i = 0, host = el;
      while (i < ws.length) {
        const m = marks.find(r => r.start === i);
        let target = el;
        if (m) {
          target = h('span', 'pp-t', { role: 'link', tabindex: '0', 'aria-haspopup': 'dialog', 'data-term': m.term.term, 'data-sec': m.term.sec, title: 'What does it mean?' });
          el.appendChild(target);
          for (let k = 0; k < m.len; k++, i++) { const w = h('span', 'w', { text: ws[i] }); list.push(w); target.appendChild(w); if (k < m.len - 1) target.appendChild(document.createTextNode(' ')); }
          if (i < ws.length) el.appendChild(document.createTextNode(' '));
          continue;
        }
        const w = h('span', 'w', { text: ws[i] }); list.push(w); host.appendChild(w);
        i++;
        if (i < ws.length) host.appendChild(document.createTextNode(' '));
      }
      sEl[s.id] = el; wEls[s.id] = list;
      return el;
    }
    const byId = {}; M.seq.forEach(s => { byId[s.id] = s; });
    const para = (arr, cls = 'pp-p') => {
      const p = h('p', cls);
      arr.forEach((s, k) => { const q = byId[s.id] || s; p.appendChild(sentence(q)); if (k < arr.length - 1) p.appendChild(document.createTextNode(' ')); });
      return p;
    };
    const heading = (s, n) => {
      const h2 = h('h2', 'pp-h2'), bt = h('button', 'pp-hbtn', { type: 'button', 'data-read': s.id, 'aria-label': 'Read from here: ' + s.text });
      if (n != null) bt.appendChild(h('span', 'pp-num', { text: String(n), 'aria-hidden': 'true' }));
      bt.appendChild(sentence(byId[s.id] || s));
      h2.appendChild(bt); return h2;
    };

    /* front matter */
    const F = M.front, head = h('header', 'pp-head');
    if (F.step.title) head.appendChild(h('p', 'pp-kicker', { text: [F.step.title, F.step.tag].filter(Boolean).join(' · ') }));
    if (F.title.id) { const t1 = h('h1', 'pp-title'); t1.appendChild(sentence(byId[F.title.id] || F.title)); head.appendChild(t1); }
    if (F.subtitle) head.appendChild(h('p', 'pp-byline', { text: F.subtitle }));
    if (F.readingTime) head.appendChild(h('p', 'pp-meta', { text: F.readingTime + ' · tap any sentence to hear it' }));
    paper.appendChild(head);
    if (F.abstract.length) {
      const ab = h('section', 'pp-abs'); ab.appendChild(h('p', 'pp-abs-k', { text: 'Abstract' }));
      ab.appendChild(para(F.abstract)); paper.appendChild(ab);
    }
    paper.appendChild(h('hr', 'pp-rule'));

    /* the sections */
    M.sections.forEach((S, si) => {
      const sec = h('section', 'pp-sec', { 'data-sec': S.id });
      if (S.heading && S.heading.id) sec.appendChild(heading(S.heading, S.n));
      if (S.figure) {
        const fg = h('figure', 'pp-fig', { 'data-sec': S.id });
        const cv = h('canvas', '', { role: 'img', 'aria-label': S.figure.alt || S.figure.title || 'figure' });
        figCanvas[S.id] = cv; fg.appendChild(cv);
        const cap = h('figcaption', '', { text: `Figure ${S.n}. ${S.figure.title || ''}` });
        cap.appendChild(h('span', 'pp-wide-note', { text: '' }));
        fg.appendChild(cap); sec.appendChild(fg);
      }
      S.paragraphs.forEach(p => sec.appendChild(para(p)));
      S.cards.forEach(cd => {
        const ask = cd.kind === 'askAway', box = h('aside', 'pp-card ' + (ask ? 'ask' : 'heads'));
        if (ask) {
          // a closed tab until tapped (PAPER.md: a student won't read margin text while the Judge talks)
          const sp = spk(cd.asker || 'rex'), n = (cd.qa || []).length;
          const tab = h('button', 'pp-ask-tab', { type: 'button', 'aria-expanded': 'false', 'data-ask': '1',
            text: `Ask Away! ${n} question${n === 1 ? '' : 's'} ${sp.name} asked` });
          const body = h('div', 'pp-ask-body'); body.hidden = true;
          (cd.qa || []).forEach(q => { const d = h('div', 'pp-qa'); d.append(h('p', 'pp-q', { text: q.q }), h('p', 'pp-a', { text: q.a })); body.appendChild(d); });
          box.append(tab, body);
        } else {
          box.appendChild(h('p', 'pp-card-k', { text: 'Heads Up!' }));
          const tx = String(cd.text || '').replace(/^Heads up:\s*/i, ''); box.appendChild(h('p', '', { text: tx.charAt(0).toUpperCase() + tx.slice(1) }));
        }
        sec.appendChild(box);
      });
      if (S.check) {
        const ck = h('div', 'pp-check', { 'data-sec': S.id, role: 'group', 'aria-label': 'Quick check, section ' + S.n });
        ck.appendChild(h('p', 'pp-check-k', { text: `Quick check · section ${S.n}` }));
        if (S.check.figure && S.figure) {
          const cv2 = h('canvas', 'pp-figcopy', { role: 'img', 'aria-label': S.figure.alt || '' });
          figCopy[S.id] = cv2; ck.appendChild(cv2);
        }
        ck.appendChild(h('p', 'pp-prompt', { text: S.check.prompt }));
        const opts = h('div', 'pp-opts'); ck.appendChild(opts);
        const why = h('p', 'pp-why', { 'aria-live': 'polite' }); ck.appendChild(why);
        const btns = h('div', 'pp-cbtns'); ck.appendChild(btns);
        checkEl[S.id] = { el: ck, opts, why, btns, sig: '' };
        sec.appendChild(ck);
      }
      secEl[S.id] = sec;
      paper.appendChild(sec);
      if (si === M.sections.length - 1) {
        /* Key terms (not narrated) */
        if (M.glossary.length) {
          const g = h('section', 'pp-terms'); g.appendChild(h('h2', 'pp-h2', { text: (M.json.glossary && M.json.glossary.heading) || 'Key terms' }));
          const dl = h('dl');
          M.glossary.forEach(t => dl.append(h('dt', '', { text: t.term }), h('dd', '', { text: t.def })));
          g.appendChild(dl); paper.appendChild(g);
        }
      }
    });

    /* You Just Learned + the score + the end */
    const sum = M.summary;
    const sumEl = h('section', 'pp-sum', { 'data-sec': 'summary' });
    if (sum.heading && sum.heading.id) sumEl.appendChild(heading(sum.heading, null));
    const ul = h('ul'); (sum.lines || []).forEach(s => { const li = h('li'); li.appendChild(sentence(byId[s.id] || s)); ul.appendChild(li); });
    sumEl.appendChild(ul);
    const scoreEl = h('p', 'pp-score'); sumEl.appendChild(scoreEl);
    const endEl = h('div', 'pp-end'); endEl.hidden = true; sumEl.appendChild(endEl);
    secEl.summary = sumEl;
    paper.appendChild(sumEl);

    /* Sources: title + publisher + the link (opens in a new tab), the URL also as text */
    if (M.sources.length) {
      const src = h('section', 'pp-src'); src.appendChild(h('h2', 'pp-h2', { text: 'Sources' }));
      const ol = h('ol');
      M.sources.forEach(s => {
        const li = h('li');
        li.appendChild(document.createTextNode(`${s.title}. ${s.publisher || ''}. `));
        const a = h('a', '', { href: s.url, target: '_blank', rel: 'noopener noreferrer', text: 'Open the source' });
        li.appendChild(a); li.appendChild(h('span', 'u', { text: s.url }));
        ol.appendChild(li);
      });
      src.appendChild(ol); paper.appendChild(src);
    }

    /* ---------------- events ---------------- */
    let keepFit = null, lastUser = 0, lastSet = 0, anim = null, touchY = null, follow = true, chipWanted = false, visible = false;
    const call = (fn, ...a) => { try { if (fn) fn(...a); } catch (e) { console.error('[paper]', e); setTimeout(() => { throw e; }); } };
    root.addEventListener('pointerdown', () => { if (window.Audio && Audio.unlock) Audio.unlock(); }, true);
    root.addEventListener('click', e => {
      const t = e.target;
      if (e.detail > 0 && t.closest && t.closest('button') && document.activeElement && root.contains(document.activeElement)) try { document.activeElement.blur(); } catch (err) { /* ignore */ }
      if (t.closest('.pp-chip')) { call(H.onBack); return; }
      const tm = t.closest('.pp-t'); if (tm) { e.preventDefault(); call(H.onTerm, tm.dataset.term, tm.dataset.sec); return; }
      const op = t.closest('.pp-opt'); if (op) { if (op.getAttribute('aria-disabled') !== 'true') call(H.onCheck, op.dataset.sec, +op.dataset.i); return; }
      const at = t.closest('[data-ask]');
      if (at) { const b = at.nextElementSibling, open = at.getAttribute('aria-expanded') !== 'true'; at.setAttribute('aria-expanded', String(open)); if (b) b.hidden = !open; call(H.onAsk, open); return; }
      const sk = t.closest('[data-skip]'); if (sk) { call(H.onSkip, sk.dataset.skip); return; }
      const cn = t.closest('[data-cont]'); if (cn) { call(H.onContinue, cn.dataset.cont); return; }
      const en = t.closest('[data-end]'); if (en) { call(H.onEnd, en.dataset.end); return; }
      const hb = t.closest('[data-read]'); if (hb) { call(H.onSentence, hb.dataset.read); return; }
      const s = t.closest('.pp-s'); if (s && !t.closest('a')) { call(H.onSentence, s.dataset.id); return; }
    });
    root.addEventListener('keydown', e => {
      const t = e.target;
      if ((e.key === 'Enter' || e.key === ' ') && t.classList && t.classList.contains('pp-t')) { e.preventDefault(); e.stopPropagation(); call(H.onTerm, t.dataset.term, t.dataset.sec); return; }
      // keys on a focused control belong to the control (Enter / Space activate it), not to the game
      if ((e.key === 'Enter' || e.key === ' ') && t.closest && t.closest('button, a')) e.stopPropagation();
    });
    tBtn.addEventListener('click', e => { R.hideTerm(); if (e.detail > 0) try { tBtn.blur(); } catch (err) { /* ignore */ } });
    term.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); });
    const user = () => { anim = null; lastUser = performance.now(); keepFit = null; if (follow || !chipWanted) { /* noop */ } call(H.onUserScroll); };
    col.addEventListener('wheel', () => user(), { passive: true });
    col.addEventListener('touchstart', e => { touchY = e.touches[0] ? e.touches[0].clientY : null; }, { passive: true });
    col.addEventListener('touchmove', e => { if (touchY != null && e.touches[0] && Math.abs(e.touches[0].clientY - touchY) > 8) { touchY = null; user(); } }, { passive: true });
    let quietUntil = 0;           // a layout change of our own (the chip strip) may clamp scrollTop: not the student
    col.addEventListener('scroll', () => {
      if (anim || performance.now() < quietUntil) { lastSet = col.scrollTop; return; }
      if (Math.abs(col.scrollTop - lastSet) > 3) { lastSet = col.scrollTop; user(); }
    }, { passive: true });

    /* ---------------- API ---------------- */
    let cur = null, curWord = -1, layoutSig = '', stageRect = null, termShown = null, figSize = {};
    const elOf = x => (typeof x === 'string' ? sEl[x] || (checkEl[x] && checkEl[x].el) || secEl[x] : x);
    const R = {
      el: root, col, paper, chip,
      layout({ rect, fs, wide, stage }) {
        stageRect = stage || null;
        const sig = [rect.left, rect.top, rect.width, rect.height, fs, wide].map(v => (typeof v === 'number' ? Math.round(v) : v)).join(',');
        if (sig === layoutSig) return false;
        const keepEl = cur && sEl[cur];
        const before = keepEl ? keepEl.getBoundingClientRect().top - col.getBoundingClientRect().top : null;
        layoutSig = sig; quietUntil = performance.now() + 200;
        Object.assign(root.style, { left: rect.left + 'px', top: rect.top + 'px', width: rect.width + 'px', height: rect.height + 'px' });
        root.style.setProperty('--fs', fs.toFixed(1) + 'px'); term.style.setProperty('--fs', fs.toFixed(1) + 'px');
        root.classList.toggle('wide', !!wide); root.classList.toggle('dock', !wide);
        if (keepEl && before != null && visible) {        // keep the reading line where it was across a resize / turn
          const now = keepEl.getBoundingClientRect().top - col.getBoundingClientRect().top;
          col.scrollTop += now - before; lastSet = col.scrollTop;
        } else lastSet = col.scrollTop;
        if (termShown) R.showTerm(termShown.term, termShown.sec);
        figSize = {};
        return true;
      },
      show(on) {
        on = !!on;
        if (on === visible) return;
        visible = on; root.classList.toggle('on', on);
        if (!on) { term.classList.remove('on'); } else if (termShown) term.classList.add('on');
        lastSet = col.scrollTop;
      },
      shown: () => visible,
      setCurrent(id, word = -1) {
        if (id !== cur) {
          if (cur && sEl[cur]) { sEl[cur].classList.remove('cur'); sEl[cur].removeAttribute('aria-current'); const ow = wEls[cur] && wEls[cur][curWord]; if (ow) ow.classList.remove('on'); }
          cur = id; curWord = -1;
          if (cur && sEl[cur]) { sEl[cur].classList.add('cur', 'read'); sEl[cur].setAttribute('aria-current', 'true'); }
        }
        if (word !== curWord && cur) {
          const L = wEls[cur] || [];
          if (L[curWord]) L[curWord].classList.remove('on');
          curWord = word; if (L[curWord]) L[curWord].classList.add('on');
        }
      },
      current: () => cur,
      setFollow(on, chipOk) { follow = !!on; chipWanted = !!chipOk; const hide = follow || !chipWanted; if (chip.hidden !== hide) { chip.hidden = hide; root.classList.toggle('chip', !hide); lastSet = col.scrollTop; quietUntil = performance.now() + 200; if (keepFit) R.reveal(keepFit, { fit: true, smooth: false }); } },
      keepInView(x) { keepFit = x || null; if (keepFit) R.reveal(keepFit, { fit: true }); },
      /* scroll so el sits at ~28 % of the column (smooth: animated by tick) */
      /* fit: the least scroll that shows the whole element (its top if it is taller than the column) */
      reveal(x, { smooth = true, at = 0.28, fit = false } = {}) {
        const el = elOf(x); if (!el) return;
        const cr = col.getBoundingClientRect(), er = el.getBoundingClientRect();
        const max = col.scrollHeight - col.clientHeight, pad = 12;
        let want = col.scrollTop + er.top - cr.top - col.clientHeight * at;
        if (fit) {
          const toTop = col.scrollTop + er.top - cr.top - pad, toBottom = col.scrollTop + er.bottom - cr.bottom + pad;
          if (er.height > cr.height - 2 * pad) {
            // taller than the column (a check that repeats its figure): show from its question down, the answers and buttons
            const q = el.querySelector && el.querySelector('.pp-prompt'), qr = q ? q.getBoundingClientRect() : er;
            want = Math.min(col.scrollTop + qr.top - cr.top - pad, toBottom);
          } else want = er.top < cr.top + pad ? toTop : er.bottom > cr.bottom - pad ? Math.min(toBottom, toTop) : col.scrollTop;
        }
        const to = Math.max(0, Math.min(max, want));
        if (Math.abs(to - col.scrollTop) < 2) { anim = null; return; }
        const reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!smooth || reduce || !visible) { anim = null; col.scrollTop = to; lastSet = col.scrollTop; return; }
        anim = { from: col.scrollTop, to, t: 0, dur: Math.min(0.9, 0.35 + Math.abs(to - col.scrollTop) / 2500) };
      },
      /* in the comfortable band of the column (not hidden under the chip / the edges) */
      visible(x, band = [0.08, 0.78]) {
        const el = elOf(x); if (!el || !visible) return false;
        const cr = col.getBoundingClientRect(), er = el.getBoundingClientRect();
        return er.top >= cr.top + cr.height * band[0] && er.bottom <= cr.top + cr.height * band[1] + Math.max(0, er.height - cr.height * 0.6);
      },
      animating: () => !!anim,
      tick(dt) {
        // a check that was just answered / is waiting stays fully in view (until the student scrolls away)
        if (!anim && keepFit && visible && performance.now() - lastUser > 400) {
          const cr = col.getBoundingClientRect(), er = keepFit.getBoundingClientRect();
          if (er.height <= cr.height - 24 && (er.top < cr.top - 1 || er.bottom > cr.bottom + 1)) R.reveal(keepFit, { fit: true });
        }
        if (!anim) return;
        anim.t += dt;
        const u = Math.min(1, anim.t / anim.dur), e = u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2;
        col.scrollTop = anim.from + (anim.to - anim.from) * e; lastSet = col.scrollTop;
        if (u >= 1) anim = null;
      },
      sectionInView() {
        if (!visible) return null;
        const cr = col.getBoundingClientRect(), line = cr.top + cr.height * 0.35;
        let best = 'front';
        for (const [id, el] of Object.entries(secEl)) { const r = el.getBoundingClientRect(); if (r.top <= line && r.bottom > line - 1) best = id; }
        return best;
      },
      setCheck(secId, st) {
        const C = checkEl[secId], S = M.sections.find(q => q.id === secId);
        if (!C || !S) return;
        const sig = JSON.stringify(st);
        if (sig === C.sig) return;
        C.sig = sig;
        const opts = S.check.options || [], order = st.order && st.order.length === opts.length ? st.order : opts.map((_, i) => i);
        const done = st.right != null, wrong = new Set(st.wrong || []);
        C.el.classList.toggle('waiting', !!st.waiting && !done && !st.skipped);
        C.opts.replaceChildren(...order.map((oi, k) => {
          const o = opts[oi], b = h('button', 'pp-opt' + (st.right === oi ? ' right' : wrong.has(oi) ? ' wrong' : ''), { type: 'button', 'data-sec': secId, 'data-i': String(oi) });
          b.append(h('span', 'n', { text: String(k + 1), 'aria-hidden': 'true' }), h('span', 't', { text: o.text }), h('span', 'mk', { text: st.right === oi ? 'RIGHT' : wrong.has(oi) ? 'NOT IT' : '' }));
          if (done || wrong.has(oi)) b.setAttribute('aria-disabled', 'true');
          b.setAttribute('aria-label', `${k + 1}. ${o.text}${st.right === oi ? ' (right)' : wrong.has(oi) ? ' (not it)' : ''}`);
          return b;
        }));
        const last = st.last != null ? opts[st.last] : null;
        C.why.replaceChildren();
        if (last) { C.why.append(h('span', last.correct ? 'ok' : 'no', { text: last.correct ? '' : 'Not quite. ' }), document.createTextNode(last.why || '')); }
        else if (st.skipped) C.why.textContent = 'Skipped. You can still answer it.';
        const bs = [];
        if (!done && !st.skipped && st.waiting) bs.push(h('button', 'pp-btn ghost', { type: 'button', 'data-skip': secId, text: 'Skip this check' }));
        if (st.waiting && (done || st.skipped)) bs.push(h('button', 'pp-btn', { type: 'button', 'data-cont': secId, text: 'Keep reading' }));
        C.btns.replaceChildren(...bs);
      },
      setScore(txt) { if (scoreEl.textContent !== txt) scoreEl.textContent = txt || ''; },
      setEnd({ show, line, again = 'Read it again', map = 'Back to the map' } = {}) {
        const sig = show ? line + '|' + again + '|' + map : '';
        if (endEl.dataset.sig === sig) return;
        endEl.dataset.sig = sig; endEl.hidden = !show;
        endEl.replaceChildren();
        if (!show) return;
        endEl.appendChild(h('p', '', { text: line || '' }));
        const bs = h('div', 'pp-cbtns');
        bs.append(h('button', 'pp-btn red', { type: 'button', 'data-end': 'map', text: map }), h('button', 'pp-btn ghost', { type: 'button', 'data-end': 'again', text: again }));
        endEl.appendChild(bs);
      },
      showTerm(t, sec) {
        const T = M.glossary.find(q => q.term === t && (!sec || q.sec === sec)) || M.glossary.find(q => q.term === t);
        if (!T) return;
        termShown = { term: T.term, sec: T.sec };
        tH.textContent = T.term; tP.textContent = T.def;
        const r = stageRect || { left: 8, top: 8, width: 320, height: 200 };
        Object.assign(term.style, { left: r.left + 'px', top: r.top + 'px', width: r.width + 'px', height: r.height + 'px', alignItems: 'center', justifyContent: 'center', padding: '6px' });
        term.classList.toggle('on', visible);
      },
      hideTerm() { termShown = null; term.classList.remove('on'); },
      termOpen: () => termShown && termShown.term,
      termEl: term,
      figure: secId => figCanvas[secId] || null,
      figureCopy: secId => figCopy[secId] || null,
      /* (re)draw the inline canvases at their CSS size (portrait); a figure redraws when its highlight changes */
      refreshFigures(hiBy = {}, force = false) {
        if (root.classList.contains('wide') || !visible) return;
        const list = [...Object.entries(figCanvas).map(([k, cv]) => [k, cv, hiBy[k] || null]), ...Object.entries(figCopy).map(([k, cv]) => [k + ':copy', cv, null])];
        for (const [k, cv, hi] of list) {
          const id = k.replace(/:copy$/, ''), S = M.sections.find(q => q.id === id);
          if (!S || !S.figure) continue;
          const w = cv.parentNode ? cv.parentNode.clientWidth || cv.clientWidth : cv.clientWidth;
          if (!w) continue;
          const fs = Math.max(11, Math.min(16, w / 24.5));
          const ht = window.PaperFigs ? PaperFigs.height(S.figure, w, fs) : Math.round(w * 0.7);
          const dpr = Math.min(3, window.devicePixelRatio || 1);
          const sig = [w, ht, dpr, JSON.stringify(hi, (key, v) => (v instanceof Set ? [...v] : v))].join('|');
          if (!force && figSize[k] === sig) continue;
          figSize[k] = sig;
          cv.style.height = ht + 'px';
          cv.width = Math.round(w * dpr); cv.height = Math.round(ht * dpr);
          const c = cv.getContext('2d');
          c.setTransform(dpr, 0, 0, dpr, 0, 0); c.clearRect(0, 0, w, ht);
          call(H.drawFigure, c, S.figure, { x: 0, y: 0, w, h: ht, fs, hi });
        }
      },
      setWideNote(txt) { root.querySelectorAll('.pp-wide-note').forEach(e => { if (e.textContent !== txt) e.textContent = txt; }); },
      sentenceEl: id => sEl[id] || null,
      checkEl: secId => (checkEl[secId] && checkEl[secId].el) || null,
      sectionEl: secId => secEl[secId] || null,
      termsAt,
      debug() {
        const cr = col.getBoundingClientRect();
        const rect = el => { if (!el) return null; const r = el.getBoundingClientRect(); return { top: Math.round(r.top - cr.top), bottom: Math.round(r.bottom - cr.top), h: Math.round(r.height) }; };
        return { shown: visible, cur, word: curWord, follow, chip: !chip.hidden, scrollTop: Math.round(col.scrollTop), height: Math.round(col.clientHeight),
          scrollHeight: col.scrollHeight, curRect: rect(cur && sEl[cur]), term: termShown && termShown.term, anim: !!anim,
          rect: { left: Math.round(cr.left), top: Math.round(cr.top), w: Math.round(cr.width), h: Math.round(cr.height) },
          termRect: term.classList.contains('on') ? (r => ({ left: Math.round(r.left), top: Math.round(r.top), w: Math.round(r.width), h: Math.round(r.height) }))(tbox.getBoundingClientRect()) : null };
      },
      destroy() { root.remove(); term.remove(); },
    };
    return R;
  }

  window.PaperReader = { model, create, findTerms, words, normW };
})();
